import { ErrorPanel } from '@backstage/core-components';
import { configApiRef, useApi, useApiHolder } from '@backstage/core-plugin-api';
import { CircularProgress } from '@material-ui/core';
import { ReactNode, useMemo } from 'react';
import { ActionsContextProvider, DataRootPathContextProvider } from './';
import { useCustomPresentation } from './useCustomPresentation';
import { CustomViewCtrls } from './ctrls';
import { CustomViewNunjucksRendererProvider } from './';

type Props = {
  id: string;
  typeId?: string;
  templateId?: string;
  data: any;
  actions?: Record<string, Function>;
  transform?: string;
  loader?: ReactNode;
};

export const CustomView = ({
  id,
  typeId,
  templateId,
  data,
  actions,
  transform,
  loader,
}: Props) => {
  const configApi = useApi(configApiRef);
  const apiHolder = useApiHolder();

  const apis = useMemo(
    () => ({ configApi, apiHolder }),
    [configApi, apiHolder],
  );

  const view = useCustomPresentation(id, typeId, templateId);

  // applies CustomViewCtrls functions to data if registered
  const transformedData = useMemo(() => {
    const transformFn = CustomViewCtrls.get(
      transform || id,
      typeId,
      templateId,
    );
    if (transformFn) return transformFn(data, apis);
    return data;
  }, [data, apis, transform, id, typeId, templateId]);

  if (view.loading) {
    return loader ?? <CircularProgress />;
  }

  if (view.error) return <ErrorPanel error={view.error} />;

  const mergedActions = Object.assign(
    view.value?.defaultActions || {},
    actions || {},
  );

  return (
    <CustomViewNunjucksRendererProvider>
      <ActionsContextProvider actions={mergedActions}>
        <DataRootPathContextProvider data={transformedData}>
          <div
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ flex: 1 }}>{view.value?.jsx}</div>
          </div>
        </DataRootPathContextProvider>
      </ActionsContextProvider>
    </CustomViewNunjucksRendererProvider>
  );
};
